/**
 * The account strategy — a one-page plan per account, modelled on the
 * "Strategic Account Plan" slide (`features/account_strategy/*.pptx`, eight
 * numbered sections). `docs/account-strategy.md` is the reference.
 *
 * Sections 1, 3, 6, 7 and 8 are written by people; 2, 4 and 5 and the header
 * are **computed on every read and never stored**. Everything both ends must
 * agree on — the request shapes, the fixed wording, and the one derivation of
 * a step's health — lives here.
 */

import { z } from 'zod';
import { actionOwnerTypeSchema, actionStatusSchema, prioritySchema } from './enums.js';
import { exportAttestationSchema } from './export-control.js';
import { dateString, mediumText, shortText, uuid } from './fields.js';
import type { AccountTeamMembership } from './authz.js';

// ---------------------------------------------------------------------------
// Fixed wording, from the slide
// ---------------------------------------------------------------------------

/** Section 3's rule line, shown as helper text. */
export const STRATEGY_OBJECTIVE_RULE = 'Success = design win + production ramp + share expansion.';

/** Section 6's rule line, shown as helper text. */
export const STRATEGY_VALUE_RULE = 'Customer value/outcome first; Menlo feature second.';

/**
 * Section 6: **five** free-text fields, one per slide prompt, in slide order.
 * `prompt` is the slide's own example line, shown as a placeholder.
 */
export const STRATEGY_VALUE_FIELDS = [
  {
    column: 'value_revenue_growth',
    label: 'Revenue / growth',
    prompt: 'Enable the customer to win new platforms, customers or market share',
  },
  {
    column: 'value_customer_success',
    label: "Customer's customer success",
    prompt: 'Improve their end-product performance / differentiation for their customers',
  },
  {
    column: 'value_economics',
    label: 'Economics',
    prompt: 'Reduce BOM / CAPEX / test cost by [X% / $X]',
  },
  {
    column: 'value_speed',
    label: 'Speed',
    prompt: 'Shorten qualification / time-to-market by [X weeks/months / %]',
  },
  {
    column: 'value_operations',
    label: 'Operations',
    prompt: 'Improve reliability, yield, uptime, SWaP-C, power or density by [X%]',
  },
] as const;

export type StrategyValueColumn = (typeof STRATEGY_VALUE_FIELDS)[number]['column'];

/** Section 8's blocker categories, exactly the slide's five. A check constraint holds the same list. */
export const STRATEGY_BLOCKER_CATEGORIES = [
  'technical',
  'price',
  'supply',
  'competitor',
  'no_exec_engagement',
] as const;
export type StrategyBlockerCategory = (typeof STRATEGY_BLOCKER_CATEGORIES)[number];
export const strategyBlockerCategorySchema = z.enum(STRATEGY_BLOCKER_CATEGORIES);

export const STRATEGY_BLOCKER_LABELS: Record<StrategyBlockerCategory, string> = {
  technical: 'Technical',
  price: 'Price',
  supply: 'Supply',
  competitor: 'Competitor',
  no_exec_engagement: 'No exec engagement',
};

/** The two optional images. Neither is required, and nothing may break without them. */
export const STRATEGY_IMAGE_SLOTS = ['product', 'competitor'] as const;
export type StrategyImageSlot = (typeof STRATEGY_IMAGE_SLOTS)[number];
export const strategyImageSlotSchema = z.enum(STRATEGY_IMAGE_SLOTS);

export const STRATEGY_IMAGE_LABELS: Record<StrategyImageSlot, string> = {
  product: 'End platform / product image',
  competitor: 'Competitor solution',
};

/**
 * The image types a strategy image may be. The route accepts the
 * INTERSECTION of this list with the Admin-editable `app_settings` allowlist,
 * so an Admin can narrow it further but never widen it past images.
 */
export const STRATEGY_IMAGE_CONTENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

/**
 * Why the per-year revenue figures on the header read "—". The slide carries
 * FY26 revenue, FY27 and FY28 forecasts and growth; `docs/analytics.md` records
 * a per-period forecast as a deliberate non-goal until
 * `opportunity_revenue_schedule` exists (§11), and FY26 revenue is parked.
 */
export const STRATEGY_FORECAST_NOTE =
  'Per-year revenue and forecasts are not calculated yet: a per-period forecast needs the ' +
  'revenue schedule deferred to v1.1, and FY26 revenue is parked. Shown as — rather than a ' +
  'number nobody defined.';

/** Section 5's content figure is descriptive. `units_per_board` takes no part in any revenue calculation. */
export const STRATEGY_CONTENT_NOTE =
  'Content per system is descriptive, not revenue: ASP × qty/system on the lines shown. ' +
  'Qty/system is optional and never derived, so where it is missing the figure is not shown.';

// ---------------------------------------------------------------------------
// Step health — the slide's "On track", derived, never stored
// ---------------------------------------------------------------------------

export const STRATEGIC_STEP_HEALTHS = ['done', 'on_track', 'at_risk', 'late'] as const;
export type StrategicStepHealth = (typeof STRATEGIC_STEP_HEALTHS)[number];

export const STRATEGIC_STEP_HEALTH_LABELS: Record<StrategicStepHealth, string> = {
  done: 'Done',
  on_track: 'On track',
  at_risk: 'At risk',
  late: 'Late',
};

/**
 * **The one place a step's health is derived.**
 *
 *  - `done` → Done;
 *  - `cancelled` → hidden (`null`);
 *  - `blocked` → At risk;
 *  - past `due_date` and not done → Late;
 *  - otherwise → On track.
 *
 * `today` is a `YYYY-MM-DD` computed by the server with the SAME expression and
 * the SAME time zone the reminder scan uses (`date(now() at time zone
 * <owner's zone>)`, `jobs/reminder-scan.ts`), so the page and the reminder
 * email never disagree about whether a step is overdue. Dates compare as
 * strings because both are ISO calendar dates.
 */
export function strategicStepHealth(
  step: { status: string; due_date: string | null },
  today: string,
): StrategicStepHealth | null {
  if (step.status === 'done') return 'done';
  if (step.status === 'cancelled') return null;
  if (step.status === 'blocked') return 'at_risk';
  if (step.due_date !== null && step.due_date < today) return 'late';
  return 'on_track';
}

// ---------------------------------------------------------------------------
// Request shapes
// ---------------------------------------------------------------------------

const requireSomething = <T extends z.ZodTypeAny>(schema: T) =>
  schema.refine(
    (b) => typeof b === 'object' && b !== null && Object.keys(b as object).length > 0,
    'an update must change something',
  );

/** Sections 1 and 6 — the stored fields of the strategy row itself. */
export const updateAccountStrategySchema = requireSomething(
  z
    .object({
      business_unit: shortText.nullable(),
      customer_exec_sponsor_contact_id: uuid.nullable(),
      menlo_exec_sponsor_user_id: uuid.nullable(),
      champion_contact_id: uuid.nullable(),
      detractor_contact_id: uuid.nullable(),
      value_revenue_growth: mediumText.nullable(),
      value_customer_success: mediumText.nullable(),
      value_economics: mediumText.nullable(),
      value_speed: mediumText.nullable(),
      value_operations: mediumText.nullable(),
    })
    .strict()
    .partial(),
);
export type UpdateAccountStrategy = z.infer<typeof updateAccountStrategySchema>;

/** Section 3: an ordered list of short bullets. */
export const strategyObjectiveSchema = z
  .object({ body: z.string().trim().min(1).max(500) })
  .strict();

/** Section 8: a category and a note. */
export const createStrategyBlockerSchema = z
  .object({
    category: strategyBlockerCategorySchema,
    note: mediumText.nullable().optional(),
  })
  .strict();
export const updateStrategyBlockerSchema = requireSomething(createStrategyBlockerSchema.partial());

/** Reorder a list: every live id, in the new order, exactly once. */
export const reorderStrategyListSchema = z
  .object({ ids: z.array(uuid).min(1).max(200) })
  .strict()
  .refine((b) => new Set(b.ids).size === b.ids.length, 'ids must not repeat');

/**
 * Section 7: a strategic next step. Stored as an `action_items` row with
 * `account_id` set, `opportunity_id` null and `is_strategic = true` — the user
 * decided against a table of its own.
 */
const strategicStepCore = z
  .object({
    title: z.string().trim().min(1).max(300),
    description: mediumText.nullable().optional(),
    due_date: dateString.nullable().optional(),
    owner_type: actionOwnerTypeSchema.optional(),
    owner_user_id: uuid.nullable().optional(),
    owner_contact_id: uuid.nullable().optional(),
    priority: prioritySchema.optional(),
    status: actionStatusSchema.optional(),
    completion_note: mediumText.nullable().optional(),
  })
  .strict();

export const createStrategicStepSchema = strategicStepCore;
export const updateStrategicStepSchema = requireSomething(strategicStepCore.partial());

/**
 * The strategy's own presign. There is no `entityType`/`entityId` in it: the
 * route supplies both, which is how a strategy image is marked at creation and
 * can never exist unmarked. It carries the same §8 item 7 attestation as every
 * other upload.
 */
export const presignStrategyImageSchema = z
  .object({
    fileName: z.string().min(1).max(255),
    contentType: z.string().min(1).max(200),
    sizeBytes: z
      .number()
      .int()
      .min(1)
      .max(5 * 1024 * 1024 * 1024),
  })
  .merge(exportAttestationSchema);

export const confirmStrategyImageSchema = z.object({ attachmentId: uuid }).strict();

// ---------------------------------------------------------------------------
// The response — one payload, computed sections and stored ones together
// ---------------------------------------------------------------------------

export interface StrategyUserRef {
  id: string;
  display_name: string;
  is_active: boolean;
}

/**
 * A referenced contact. A contact soft-deleted or redacted AFTER being chosen
 * stays referenced and renders as such — never as a blank or an error.
 */
export interface StrategyContactRef {
  id: string;
  name: string;
  title: string | null;
  disposition: string;
  is_deleted: boolean;
  is_redacted: boolean;
  is_active: boolean;
}

export interface StrategyMoneyFigure {
  /** `numeric` as a string (§9: money is never a float). */
  value: string;
  count: number;
  basis: 'net';
  basisLabel: string;
  note: string;
}

export interface StrategyProjectRow {
  opportunity_id: string;
  opp_number: string | null;
  name: string;
  status: string;
  stage_code: string;
  /** `programs.name`, else `opportunities.end_product`. */
  platform: string | null;
  /** `opportunities.application`. */
  use_case: string | null;
}

export interface StrategyContentLine {
  id: string;
  part_number: string;
  line_type: string;
  asp: string | null;
  units_per_board: number | null;
  /** On an OPEN opportunity whose stage is below DESIGN_WIN. */
  is_future: boolean;
}

export interface StrategyContentGroup {
  opportunity_id: string;
  opp_number: string | null;
  name: string;
  status: string;
  price_basis: string;
  /** Per opportunity, never averaged across them. */
  gross_margin_pct: string | null;
  lines: StrategyContentLine[];
  /** `sum(asp × units_per_board)`, only when EVERY line has `units_per_board`. */
  content_per_system: string | null;
  content_per_system_reason: string | null;
}

export interface StrategyStepRow {
  id: string;
  title: string;
  description: string | null;
  due_date: string | null;
  status: string;
  priority: string;
  owner_type: 'internal' | 'customer';
  owner_user_id: string | null;
  owner_user: StrategyUserRef | null;
  owner_contact_id: string | null;
  owner_contact: StrategyContactRef | null;
  completed_at: string | null;
  completion_note: string | null;
  health: StrategicStepHealth;
  updated_at: string;
}

export interface StrategyImageRef {
  attachmentId: string;
  fileName: string;
  contentType: string;
}

export interface AccountStrategyView {
  account: {
    id: string;
    name: string;
    owner_user_id: string | null;
    owner: StrategyUserRef | null;
  };
  /** Computed server-side; the client feeds it to `canWriteAccountStrategy`. */
  membership: AccountTeamMembership;
  /** False until someone first writes to it. Every section still renders. */
  exists: boolean;
  header: {
    updatedAt: string | null;
    openPipeline: StrategyMoneyFigure;
    wonBacklog: StrategyMoneyFigure;
    fy26Revenue: null;
    fy27Forecast: null;
    fy28Forecast: null;
    growth: null;
    forecastNote: string;
  };
  relationship: {
    business_unit: string | null;
    customer_exec_sponsor: StrategyContactRef | null;
    menlo_exec_sponsor: StrategyUserRef | null;
    champion: StrategyContactRef | null;
    detractor: StrategyContactRef | null;
  };
  /** Live, non-deleted, non-redacted contacts on this account, for the pickers. */
  contacts: StrategyContactRef[];
  coverage: { sales_lead: StrategyUserRef | null; faes: StrategyUserRef[] };
  objectives: { id: string; body: string; sort_order: number }[];
  projects: {
    program_id: string | null;
    program_name: string | null;
    rows: StrategyProjectRow[];
  }[];
  content: { groups: StrategyContentGroup[]; note: string };
  value: Record<StrategyValueColumn, string | null>;
  steps: StrategyStepRow[];
  /** Cancelled steps are hidden; this says how many, so nothing vanishes silently. */
  hiddenCancelledSteps: number;
  blockers: {
    id: string;
    category: StrategyBlockerCategory;
    note: string | null;
    sort_order: number;
  }[];
  images: Record<StrategyImageSlot, StrategyImageRef | null>;
}

/** One row of the strategy list: every account the caller can read a strategy for. */
export interface AccountStrategyListRow {
  account_id: string;
  account_name: string;
  owner_name: string | null;
  /** Null when no strategy row exists yet — the account is listed regardless. */
  strategy_updated_at: string | null;
  membership: AccountTeamMembership;
}
